import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { API } from '../../../../../../api';
import { getLLAuthHeaders } from '../../../../utils/llAuthHeaders';
import { eventTracking, events } from '../../../../../../utils/amplitude';
import {
  mapApiGap,
  type Gap,
  type RecommendationKey,
  type RecommendationStatus,
  type TeamGaps
} from './knowledgeGaps';
import { isCapabilityRelease } from '../../../../../../utils/capabilityRelease';

// What the gaps are called: Capability Gaps with the Capability release (utils/capabilityRelease.ts).
const gapsName = () => (isCapabilityRelease() ? 'Capability Gaps' : 'Learning Gaps');

export interface LearningGapsData {
  loading: boolean;
  error: string | null;
  /** Null once loaded means the org has no Teams list marked. */
  teamsList: { listId: string; listName: string } | null;
  teams: TeamGaps[];
  lastComputedAt: number | null;
  refreshing: boolean;
  /** Persist a tick (null clears it). Optimistic; the BE's answer wins. */
  setStatus: (
    gapId: string,
    episodeId: string,
    key: Exclude<RecommendationKey, 'assessment'>,
    next: RecommendationStatus | null
  ) => Promise<void>;
  /** Run the engine now, then reload. */
  refresh: () => Promise<void>;
  reload: () => Promise<void>;
}

const readError = (e: any, fallback: string): string =>
  e?.response?.data?.message || e?.message || fallback;

const toGap = (raw: any): Gap => mapApiGap(raw);

/**
 * The Learning Gaps panel's data — GET /v3/admin/learning-gaps behind the
 * `learningGaps` flag (`enabled`). Ticks go straight to the BE and the gap
 * returned replaces the local one, so what's on screen is what's stored.
 *
 * `orgId` names the org for platform admins — the BE refuses them without it.
 * Leave it undefined for everyone else; their JWT carries the org.
 */
export const useLearningGapsData = (enabled: boolean, orgId?: string): LearningGapsData => {
  // Starts loading when enabled, so the panel's first frame is its loading
  // placeholder rather than a flash of "No Teams list configured".
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [teamsList, setTeamsList] = useState<{ listId: string; listName: string } | null>(null);
  const [teams, setTeams] = useState<TeamGaps[]>([]);
  const [lastComputedAt, setLastComputedAt] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const res: any = await API.getLearningGaps({ ...getLLAuthHeaders(), orgId });
      const data = res?.data?.data ?? res?.data ?? {};
      if (!alive.current) return;
      setTeamsList(data.teamsList ?? null);
      setTeams(
        (Array.isArray(data.teams) ? data.teams : []).map((t: any) => ({
          id: String(t.id),
          name: String(t.name),
          gaps: (Array.isArray(t.gaps) ? t.gaps : []).map(toGap)
        }))
      );
      setLastComputedAt(typeof data.lastComputedAt === 'number' ? data.lastComputedAt : null);
    } catch (e: any) {
      console.error('[LearningGaps] load failed', e);
      if (alive.current) setError(readError(e, `${gapsName()} could not be loaded. Refresh the page and try again.`));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [enabled, orgId]);

  useEffect(() => {
    if (!enabled) {
      setTeams([]);
      setTeamsList(null);
      setLastComputedAt(null);
      return;
    }
    void reload();
  }, [enabled, reload]);

  // Admins and team managers act on the same gaps from their own dashboards:
  // coming back to the tab picks up anyone else's ticks. At most every 30s.
  useEffect(() => {
    if (!enabled) return undefined;
    let lastLoad = Date.now();
    const onReturn = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastLoad < 30000) return;
      lastLoad = Date.now();
      reload();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [enabled, reload]);

  const replaceGap = useCallback((updated: Gap) => {
    setTeams((prev) =>
      prev.map((t) => ({ ...t, gaps: t.gaps.map((g) => (g.id === updated.id ? updated : g)) }))
    );
  }, []);

  const setStatus = useCallback<LearningGapsData['setStatus']>(
    async (gapId, episodeId, key, next) => {
      // Optimistic tick so the checkbox answers the click.
      let snapshot: TeamGaps[] = [];
      setTeams((prev) => {
        snapshot = prev;
        return prev.map((t) => ({
          ...t,
          gaps: t.gaps.map((g) =>
            g.id !== gapId
              ? g
              : {
                  ...g,
                  episodes: g.episodes.map((ep) => {
                    if (ep.id !== episodeId) return ep;
                    const status = { ...ep.status };
                    if (next) status[key] = next;
                    else delete status[key];
                    return { ...ep, status };
                  })
                }
          )
        }));
      });
      try {
        const res: any = await API.updateLearningGapRecommendation({
          gapId,
          episodeId,
          key,
          state: next,
          orgId,
          ...getLLAuthHeaders()
        });
        const data = res?.data?.data ?? res?.data ?? {};
        if (data.gap) {
          const gap = toGap(data.gap);
          replaceGap(gap);
          if (data.closed) {
            const episode = gap.episodes.find((ep) => ep.id === episodeId);
            const team = teams.find((t) => t.gaps.some((g) => g.id === gapId));
            eventTracking(events.knowledgeGapClosed, {
              gapId,
              gapTitle: gap.title,
              gapKind: gap.kind,
              programName: gap.programName ?? undefined,
              team: team?.name,
              episode: episode?.n
            });
          }
        }
      } catch (e: any) {
        console.error('[LearningGaps] status update failed', e);
        toast.error(readError(e, 'That change could not be saved. Try again.'));
        setTeams(snapshot);
      }
    },
    [replaceGap, teams, orgId]
  );

  const refresh = useCallback(async () => {
    if (!enabled || refreshing) return;
    setRefreshing(true);
    try {
      const res: any = await API.refreshLearningGaps({ ...getLLAuthHeaders(), orgId });
      const summary = res?.data?.data ?? res?.data ?? {};
      if (summary.skipped === 'no-teams-list') {
        toast.info('Mark one of your attribute lists as Teams in Organization Settings first.');
      } else if (summary.skipped === 'no-programs') {
        toast.info('No published programs to look through yet.');
      } else if (typeof summary.episodesOpened === 'number') {
        toast.success(
          summary.episodesOpened > 0
            ? `${summary.episodesOpened} new ${summary.episodesOpened === 1 ? 'gap' : 'gaps'} found.`
            : 'No new gaps since the last check.'
        );
      }
      await reload();
    } catch (e: any) {
      console.error('[LearningGaps] refresh failed', e);
      toast.error(readError(e, `Could not refresh ${gapsName()}.`));
    } finally {
      if (alive.current) setRefreshing(false);
    }
  }, [enabled, refreshing, reload, orgId]);

  return { loading, error, teamsList, teams, lastComputedAt, refreshing, setStatus, refresh, reload };
};
